import { ReactNode, useCallback } from 'react';
import { State } from '../BannerSlider';

type Props = {
    itemWidth: number;
    initialSlide: boolean;
    showButtons: boolean;
    centerMode: boolean;
    showDots: boolean;
    children: ReactNode | undefined;
};

export const useUpdateItems = ({ itemWidth, initialSlide, showButtons, centerMode, showDots, children }: Props) => {
    const updateItems = useCallback(
        (parentWidth: number) => {
            const newState = {} as Omit<State, 'parentWidth' | 'mounted' | 'lastIndexClick'>;
            let array: Array<ReactNode> = [];

            const childrenCount = Array.isArray(children) ? children.length : 0;

            if (childrenCount * itemWidth <= parentWidth) {
                newState.bannerGreaterThanScreen = false;
                newState.initialSlide = false;
                newState.showButtons = false;
                newState.centerMode = true;
                newState.showDots = false;
            } else {
                newState.bannerGreaterThanScreen = true;
                newState.initialSlide = initialSlide;
                newState.showButtons = showButtons;
                newState.centerMode = centerMode;
                newState.showDots = showDots;
            }

            if (childrenCount * itemWidth > parentWidth) {
                newState.visibleItems = Math.ceil(parentWidth / itemWidth) + 1;
                if (2 * itemWidth > parentWidth) {
                    newState.neededItems = childrenCount + 5;
                } else {
                    newState.neededItems = childrenCount * 2 + 2;
                }
            } else {
                newState.visibleItems = childrenCount;
                newState.neededItems = newState.visibleItems;
            }
            const items: ReactNode[] = Array.isArray(children) ? children : [];
            if (items.length) {
                const maxLength = items.length ?? 0;
                array = items.slice();
                if (newState.visibleItems < newState.neededItems) {
                    array.splice(0, 0, items[maxLength - 1]);
                    if (maxLength > 1) {
                        array.splice(0, 0, items[maxLength - 2]);
                    }
                    let i = 0;
                    while (array.length < newState.neededItems) {
                        array.push(items[i]);
                        i += 1;
                        // prevent invalid index, reset iterator to loop through items
                        if (i >= items.length) {
                            i = 0;
                        }
                    }
                }
            }

            newState.items = array;
            return newState;
        },
        [centerMode, initialSlide, itemWidth, children, showButtons, showDots]
    );

    return { updateItems };
};
